import { DateTime } from "luxon";
import { MRT_Row } from "material-react-table";
import { FleetEventRow } from "../FleetManagementEventDetailTable.component";

/**
 * Event is editable until it is checked-out or the end time is in the past.
 * (Checked-out state may be inferred by backend; here we use end time.)
 */
export const isEventEditable = (row: MRT_Row<FleetEventRow>, timezone: string): boolean => {
  if (!row.original.end) return false;

  const now = DateTime.now().setZone(timezone);
  const end = DateTime.fromISO(row.original.end, { zone: "utc" }).setZone(timezone);

  return end > now;
};

/** Start time is not editable when the event is checked-in. */
export const isStartTimeEditable = (row: MRT_Row<FleetEventRow>, timezone: string): boolean =>
  isEventEditable(row, timezone) && !row.original.isCheckedIn;

export const isFleetEventRowEditable = (event: FleetEventRow, timezone: string): boolean => {
  if (!event.end) return false;
  const now = DateTime.now().setZone(timezone);
  const end = DateTime.fromISO(event.end, { zone: "utc" }).setZone(timezone);
  return end > now;
};

export const isFleetEventStartEditable = (event: FleetEventRow, timezone: string): boolean =>
  isFleetEventRowEditable(event, timezone) && !event.isCheckedIn;

export const isEventNotStarted = (start: string | undefined, timezone: string): boolean => {
  if (!start) return false;
  const now = DateTime.now().setZone(timezone);
  const eventStart = DateTime.fromISO(start).setZone(timezone);
  return eventStart > now;
};

/**
 * Schedule-like object (e.g. BookingScheduleInterface) for calendar/schedule views.
 * Same editable logic as Fleet table: end in future; start also requires !checkedIn.
 */
export type VehicleScheduleTimeEditableSchedule = {
  end?: string;
  endDate?: string;
  checkedIn?: boolean;
  /** When true, trip is completed; time and trip reason should not be editable. */
  checkedOut?: boolean;
};

/** End time is editable when event end is in the future and not checked out (same as isEventEditable). */
export const isVehicleScheduleEndEditable = (
  schedule: VehicleScheduleTimeEditableSchedule,
  timezone: string
): boolean => {
  if (schedule.checkedOut) return false;
  const endIso = schedule.end ?? schedule.endDate;
  if (!endIso) return false;
  const now = DateTime.now().setZone(timezone);
  const end = DateTime.fromISO(endIso, { zone: "utc" }).setZone(timezone);
  return end > now;
};

/** Start time is editable when end is in future, not checked in, and not checked out (same as isStartTimeEditable). */
export const isVehicleScheduleStartEditable = (
  schedule: VehicleScheduleTimeEditableSchedule,
  timezone: string
): boolean =>
  isVehicleScheduleEndEditable(schedule, timezone) && !schedule.checkedIn && !schedule.checkedOut;
